import React, { useState, useEffect, useCallback, FC, useRef, useMemo, ReactElement } from 'react';
import { Graph } from '@visx/network';
import { PageFlow as PageFlowProto } from '@fullstory/proto/fs/api/metrics/pagemetrics';
import { int64 } from '@fullstory/proto/google/protobuf';
import {
  BOX_WIDTH,
  JOINT_WIDTH,
  BOX_LEFT_RIGHT_MARGIN,
  CHART_WIDTH,
  CHART_HEIGHT,
  SPECIAL_ID_OTHER,
  MAX_TITLE_WIDTH,
} from './const';
import { GraphNode, GraphLink, Link, Node, PageType, PageTypes } from './types';
import {
  mapPageFlowNodesInOrder,
  generateLinkStrokeWidths,
  generateEntryLink,
  generateExitLink,
  generateInternalId,
} from './util';
import { PageFlowLink } from './Link';
import { PageFlowNode } from './Node';
import { PageFlowHeader } from './Header';
import { PageFlowContext } from './hooks';
import { EmptyDiagram } from './EmptyDiagram';
import { useLocation } from 'react-router-dom';
import css from './index.module.scss';
import { Box, InlineLink, Pill, Text } from '@fullstory/components';
import { getPercentString } from '../../util/fmt';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { useOtherPagesModal } from '../../hooks/useOtherPagesModal';
import { PAGE_FLOW_TOP_PAGES_COUNT } from '../../constants';
import { MiddleEllipsis } from './MiddleEllipsis';
import { trackClickedPageFlowNode } from './trackers';

export interface PageFlowProps {
  page: PageFlowProto | null;
  getPageDefTooltip: (pageDefId: number) => React.ReactNode;
  getPageName: (pageDefId: number) => string;
  width?: number;
  isEdit?: boolean;
  onWatchSessions: (pageDefId?: number, type?: PageType) => void;
  viewMetric?: () => void;
  pageDefinition?: PageDefinition | undefined;
}

/** https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback */
function useEventCallback<A extends unknown[], R>(
  fn: (...args: A) => R,
  dependencies: Array<any>,
): (...args: A) => R {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  }, [fn, ...dependencies]); // eslint-disable-line react-hooks/exhaustive-deps
  return useCallback((...args) => ref.current(...args), [ref]);
}

export const PageFlow: FC<PageFlowProps> = ({
  page,
  isEdit,
  width = CHART_WIDTH,
  getPageDefTooltip,
  getPageName,
  onWatchSessions,
  viewMetric,
  pageDefinition,
}) => {
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);

  const location = useLocation();
  const intl = useIntl();

  const chartMargins = Math.floor(Math.max(width ? (width - CHART_WIDTH) / 2 : 0, 16));

  const orderedPageNodes = mapPageFlowNodesInOrder(page, selectedNode?.internalId);

  const entryNodes: GraphNode[] = useMemo(
    () => orderedPageNodes?.entry ?? [],
    [orderedPageNodes?.entry],
  );
  const pageNode = orderedPageNodes?.center;
  const exitNodes: GraphNode[] = useMemo(
    () => orderedPageNodes?.exit ?? [],
    [orderedPageNodes?.exit],
  );
  const incomingEdgeCount = int64.toNumber(page?.incomingEdgeCount);
  const outgoingEdgeCount = int64.toNumber(page?.outgoingEdgeCount);

  const otherEventsModal = useOtherPagesModal({
    entryOther: orderedPageNodes?.entryOther || [],
    exitOther: orderedPageNodes?.exitOther || [],
    getPageNameById: getPageName,
    pageName: page ? getPageName(page.pageDefId) : '',
    incomingCount: int64.toNumber(page?.totalIncomingCount),
    incomingEdgeCount,
    outgoingCount: int64.toNumber(page?.totalOutgoingCount),
    outgoingEdgeCount,
    onClose: () => setSelectedNode(null),
  });

  const onSelect = useEventCallback(
    (id: string) => {
      if (entryNodes && exitNodes) {
        const node = [...entryNodes, ...exitNodes].find(n => n.internalId === id) ?? null;
        setSelectedNode(node);
      }
    },
    [entryNodes, exitNodes],
  );

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const queryId = qs.get('pageDefId');
    const queryType = qs.get('type') as PageTypes;
    const selectedQueryNodeId =
      queryId && queryType ? generateInternalId(parseInt(queryId, 10), queryType) : undefined;
    selectedQueryNodeId && onSelect(selectedQueryNodeId);
  }, [location.search, onSelect]);

  let graphicWidth = Math.floor(width);
  if (CHART_WIDTH > graphicWidth) {
    graphicWidth = CHART_WIDTH + chartMargins;
  }

  const minEntryWidth = 5;

  const entryStrokeWidths: number[] = useMemo(
    () => generateLinkStrokeWidths(entryNodes, int64.toNumber(page?.count), minEntryWidth),
    [entryNodes, page, minEntryWidth],
  );
  const exitStrokeWidths: number[] = useMemo(
    () => generateLinkStrokeWidths(exitNodes, int64.toNumber(page?.count), minEntryWidth),
    [exitNodes, page, minEntryWidth],
  );

  if (!page || !pageNode) {
    return <EmptyDiagram width={graphicWidth} />;
  }

  const totalEntryFlow = int64.toNumber(page?.totalIncomingCount);
  const totalExitFlow = int64.toNumber(page?.totalOutgoingCount);

  // total width of all the paths from entry and exit pages as a proportion of the height of the page flow node
  const entryPageVisitWidth = entryStrokeWidths.reduce((sum, p) => sum + p, 0);
  const exitPageVisitWidth = exitStrokeWidths.reduce((sum, p) => sum + p, 0);

  const links: GraphLink[] = [
    ...entryNodes.map((node, i) =>
      generateEntryLink(
        node,
        pageNode,
        entryStrokeWidths[i] ?? 0,
        entryStrokeWidths.slice(0, i).reduce((sum, w) => sum + w, 0),
        entryPageVisitWidth,
      ),
    ),
    ...exitNodes.map((node, i) =>
      generateExitLink(
        node,
        pageNode,
        exitStrokeWidths[i] ?? 0,
        exitStrokeWidths.slice(0, i).reduce((sum, w) => sum + w, 0),
      ),
    ),
  ];
  const data = {
    nodes: [...entryNodes, pageNode, ...exitNodes],
    links,
  };

  let height = CHART_HEIGHT;
  if (entryNodes.length > 5 || exitNodes.length > 5) {
    height += 100;
  }

  const onSelectNode = (node: GraphNode | null) => {
    const isOtherNode = node?.pageDefId === SPECIAL_ID_OTHER;
    if (node) {
      trackClickedPageFlowNode({
        isOtherNode,
        entryOrExit: node.type,
        totalOther:
          (node.type === PageTypes.ENTRY ? incomingEdgeCount : outgoingEdgeCount) -
          PAGE_FLOW_TOP_PAGES_COUNT,
      });
    }

    if (isOtherNode) {
      otherEventsModal.setEntryOrExit(node?.type as PageTypes);
      setSelectedNode(node);
    } else if (!isOtherNode && !isEdit) {
      setSelectedNode(prev => (prev?.internalId === node?.internalId ? null : node));
    }
  };

  const onHoverNode = (node: GraphNode | null) => {
    if (!isEdit) {
      setHoveredNode(prev => (prev?.internalId === node?.internalId ? null : node));
    }
  };

  return (
    <>
      <PageFlowContext.Provider
        value={{
          onWatchSessions,
          isEdit: Boolean(isEdit),
          page,
          selectedNode,
          onSelectNode,
          hoveredNode,
          onHoverNode,
          getPageDefTooltip,
          pageDefinition,
        }}
      >
        <svg
          data-component="PageFlow"
          width={graphicWidth}
          height={height}
          viewBox={`0 0 ${graphicWidth} ${height}`}
          className={css.block}
        >
          <defs>
            <filter id="pageNodeFilter">
              <feDropShadow floodOpacity={0.2} floodColor="currentcolor" />
            </filter>
          </defs>
          <rect width="100%" height={height} className={css.visualizationBackground} />
          <g className={css.diagramBody} style={{ transform: `translate(${chartMargins}px, 0)` }}>
            {pageNode && (
              <>
                <foreignObject x={BOX_LEFT_RIGHT_MARGIN} width={BOX_WIDTH - 12} height={200}>
                  <PageFlowHeader
                    name={intl.formatMessage({ defaultMessage: 'Previous Page', id: '6TbqAD' })}
                    visits={totalEntryFlow}
                    visitsPct={getPercentString(totalEntryFlow, int64.toNumber(page.count))}
                  />
                </foreignObject>

                <foreignObject x={pageNode.x} width={BOX_WIDTH - 12} height={200}>
                  <PageFlowHeader
                    name={intl.formatMessage({ defaultMessage: 'This Page', id: '0JHX4D' })}
                    visits={int64.toNumber(pageNode.count)}
                    visitsPct={getPercentString(
                      int64.toNumber(page.count),
                      int64.toNumber(page.count),
                    )}
                  />
                </foreignObject>

                <foreignObject
                  x={pageNode.x * 2 - (JOINT_WIDTH + BOX_LEFT_RIGHT_MARGIN)}
                  width={BOX_WIDTH - 12}
                  height={200}
                >
                  <PageFlowHeader
                    name={intl.formatMessage({ defaultMessage: 'Next Page', id: 'uuLnK5' })}
                    visits={totalExitFlow}
                    visitsPct={getPercentString(totalExitFlow, int64.toNumber(page.count))}
                  />
                </foreignObject>
              </>
            )}

            <Graph
              graph={data}
              linkComponent={(link: Link) => <PageFlowLink link={link.link} />}
              nodeComponent={(node: Node) => {
                let value: string | ReactElement;

                if (node.node.pageDefId === SPECIAL_ID_OTHER) {
                  const basePageCount =
                    node.node.type === PageTypes.ENTRY
                      ? page.incomingEdges.length
                      : page.outgoingEdges.length;
                  const otherPageCount = basePageCount - PAGE_FLOW_TOP_PAGES_COUNT;
                  value = (
                    <InlineLink style={{ cursor: 'pointer' }}>
                      {intl.formatMessage(
                        { defaultMessage: '{otherPageCount, number} more pages', id: '1ngXNZ' },
                        { otherPageCount },
                      )}
                    </InlineLink>
                  );
                } else {
                  value = getPageName(node.node.pageDefId);
                }

                const nameElement = (
                  <MiddleEllipsis
                    maxLines={2}
                    maxWidth={MAX_TITLE_WIDTH}
                    appearance="bodySmallBold"
                    color="default"
                  >
                    {value}
                  </MiddleEllipsis>
                );

                return (
                  <PageFlowNode
                    name={nameElement}
                    node={node.node}
                    maxEntryWidth={entryPageVisitWidth}
                    maxExitWidth={exitPageVisitWidth}
                    viewMetric={viewMetric}
                  />
                );
              }}
            />
          </g>
          {isEdit && (
            <>
              <rect className={css.previewMode} width="100%" height={height} />
              <g className={css.previewModeTooltip}>
                <foreignObject x={(graphicWidth - 270) / 2} y={height / 2} width={270} height={70}>
                  <Pill color="default" variant="primary">
                    <Box padding={[8, 20]}>
                      <Text appearance="bodySmall" color="default">
                        <FormattedMessage
                          defaultMessage="Save the card to explore this Page Flow."
                          id="r8e/vQ"
                        />
                      </Text>
                    </Box>
                  </Pill>
                </foreignObject>
              </g>
              <g>
                <foreignObject x={graphicWidth - 120} y="25" width={75} height={25}>
                  <Pill color="secondary" variant="primary">
                    <Text appearance="bodySmall" color="default">
                      <FormattedMessage defaultMessage="PREVIEW" id="Isedjj" />
                    </Text>
                  </Pill>
                </foreignObject>
              </g>
            </>
          )}
        </svg>
      </PageFlowContext.Provider>
      {otherEventsModal.ModalComponent}
    </>
  );
};
